/**
 * Stage Plot Draft Generator — pure, deterministic, dependency-free.
 *
 * Turns a band's REAL musicians/instruments, crew and backline into a draft
 * StagePlot (elements placed by a zone-based rule engine) plus a human-readable
 * report. No React, no fetch, no LLM — runs fully offline and is unit-testable
 * outside the DOM (same pattern as src/lib/aiImport.ts).
 *
 * RULE: every produced element's `iconId` MUST belong to `PALETTE_ITEMS` (the
 * closed palette in src/shared/types/stageplot.ts). The tests assert this
 * programmatically.
 */

import type { StagePlot, StageElement, StageDimensions, PaletteCategory } from '../shared/types/stageplot';
import { PALETTE_ITEMS, CATEGORY_COLORS, createEmptyStagePlot } from '../shared/types/stageplot';
import { iconForInstrumentName, ampIconForInstrumentName, iconForRole } from './stageInstrumentIcons';
import { normalizeElementConfig } from './stageplotElements';
import { defaultRisers, type RiserConfig } from '../components/StagePlotEditor/riserConfig';

/** The closed set of valid palette iconIds (for safety + tests). */
export const VALID_PALETTE_ICON_IDS: ReadonlySet<string> = new Set(
  PALETTE_ITEMS.map((i) => i.iconId),
);

/** Look up a palette item by iconId (undefined if not in the closed palette). */
function paletteItem(iconId: string) {
  return PALETTE_ITEMS.find((i) => i.iconId === iconId);
}

// ──────────────────────────────────────────
// Input types
// ──────────────────────────────────────────

export interface DraftMusician {
  id: string;
  name: string;
  instruments: { name: string }[];
}

export interface DraftCrewMember {
  name: string;
  role: string;
}

export interface DraftBacklineItem {
  label: string;
  type: string;
  quantity: number;
}

export interface DraftRiser {
  name: string;
  /** Dimension in meters as a number, or a text like "1m" / "0,60m" / "0.40m". */
  width: number | string;
  depth: number | string;
  height: number | string;
  position: string;
  /** Number of identical platforms (defaults to 1). May be a text quantity. */
  quantity?: number | string;
}

/**
 * Parse a stage-riser dimension into METERS. Accepts a plain number (assumed
 * meters) or French/English text like "1m", "0,60m", "0.40m", "60cm", "6ft"
 * (comma or dot decimal). Returns the fallback when the value is missing,
 * malformed or not positive — the caller decides what "not parseable" means.
 */
export function parseRiserDimToMeters(v: number | string | undefined, fallback: number): number {
  if (typeof v === 'number') return Number.isFinite(v) && v > 0 ? v : fallback;
  if (typeof v !== 'string') return fallback;
  const s = v.trim().toLowerCase().replace(',', '.');
  const m = s.match(/^(-?(?:\d+(?:\.\d*)?|\.\d+))\s*(m|cm|ft)?$/);
  if (!m) return fallback;
  const num = parseFloat(m[1]);
  if (!Number.isFinite(num) || num <= 0) return fallback;
  const unit = m[2];
  if (unit === 'cm') return num / 100;
  if (unit === 'ft') return num * 0.3048;
  return num; // bare number or meters
}

/** Build a canonical valid RiserConfig from real riser data (size in cm). */
function parseRiserQuantity(q: number | string | undefined): number {
  if (typeof q === 'number' && Number.isFinite(q)) return q;
  if (typeof q === 'string') {
    const n = parseFloat(q.replace(',', '.'));
    if (Number.isFinite(n)) return n;
  }
  return 1;
}

function riserConfigFromDraft(r: DraftRiser): RiserConfig {
  const w = parseRiserDimToMeters(r.width, 0);
  const d = parseRiserDimToMeters(r.depth, 0);
  const h = parseRiserDimToMeters(r.height, 0);
  const qty = Math.max(1, Math.min(12, Math.round(parseRiserQuantity(r.quantity))));
  // Not parseable / not positive → the canonical default (renderer-safe).
  if (w <= 0 || d <= 0 || h <= 0) return defaultRisers();
  return {
    count: qty,
    uniform: true,
    size: { width: Math.round(w * 100), depth: Math.round(d * 100), height: Math.round(h * 100) },
    items: [],
    onWheels: false,
  };
}

export interface StagePlotDraftInput {
  bandName: string;
  stageDimensions: StageDimensions;
  musicians: DraftMusician[];
  crew: DraftCrewMember[];
  backlineItems: DraftBacklineItem[];
  risers?: DraftRiser[];
  plotName?: string;
}

// ──────────────────────────────────────────
// Report types
// ──────────────────────────────────────────

export interface UnmappedItem {
  name: string;
  reason: string;
}

export interface StageReport {
  unmapped: UnmappedItem[];
  warnings: string[];
  placedCount: number;
  stageTooSmall: boolean;
  neededArea?: string;
  overflowCount?: number;
}

// ──────────────────────────────────────────
// Placement helpers
// ──────────────────────────────────────────

interface Placed {
  el: StageElement;
  /** Axis-aligned bounding box in stage units (ignores rotation for overlap). */
  left: number;
  top: number;
  right: number;
  bottom: number;
}

function overlaps(a: Placed, b: Placed): boolean {
  return !(a.right <= b.left || b.right <= a.left || a.bottom <= b.top || b.bottom <= a.top);
}

/**
 * Find the first free position for a box of `bw`×`bh` starting at (x,y),
 * sliding vertically then horizontally until it fits without overlapping any
 * placed element and stays inside the stage bounds. Returns null if no free
 * spot exists (stage too small).
 */
function firstFree(
  placed: Placed[],
  x: number,
  y: number,
  bw: number,
  bh: number,
  stageW: number,
  stageH: number,
): { x: number; y: number } | null {
  const step = 0.1;
  const maxX = Math.max(0, stageW - bw);
  const maxY = Math.max(0, stageH - bh);
  for (let cy = y; cy <= maxY + 1e-6; cy += step) {
    for (let cx = x; cx <= maxX + 1e-6; cx += step) {
      const cand: Placed = { el: null as unknown as StageElement, left: cx, top: cy, right: cx + bw, bottom: cy + bh };
      if (!placed.some((p) => overlaps(p, cand))) {
        return { x: cx, y: cy };
      }
    }
  }
  return null;
}

/** Per-call sequence counter for deterministic element ids. */
let idSeq = 0;

function makeElement(
  iconId: string,
  label: string,
  x = 0,
  y = 0,
  color?: string,
): StageElement {
  const item = paletteItem(iconId);
  const category: PaletteCategory = item?.category ?? 'stage';
  return {
    id: `gen-${iconId}-${idSeq++}`,
    iconId,
    category,
    label,
    x,
    y,
    width: item?.defaultWidth ?? 1,
    height: item?.defaultHeight ?? 1,
    rotation: 0,
    color: color ?? CATEGORY_COLORS[category],
  };
}

// ──────────────────────────────────────────
// Zone-based placement engine
// ──────────────────────────────────────────

/**
 * Generate a deterministic draft stage plot from real band data.
 *
 * Placement zones (in stage units, origin top-left, +y toward the front of
 * the stage):
 *   - back row: risers, drum kit, distro/rack (garden side)
 *   - mid row: keyboards, amps behind their player
 *   - front row: vocals / front line
 *   - lateral: sidefills, monitor consoles (garden side)
 *   - front edge: wedges in a line
 */
export function generateStagePlotDraft(input: StagePlotDraftInput): { plot: StagePlot; report: StageReport } {
  const { width, height } = input.stageDimensions;
  const stageW = Math.max(0, width);
  const stageH = Math.max(0, height);
  idSeq = 0; // deterministic ids per call
  const report: StageReport = {
    unmapped: [],
    warnings: [],
    placedCount: 0,
    stageTooSmall: false,
  };

  const placed: Placed[] = [];
  const elements: StageElement[] = [];
  const add = (el: StageElement, x: number, y: number): boolean => {
    const bw = el.width;
    const bh = el.height;
    const spot = firstFree(placed, x, y, bw, bh, stageW, stageH);
    if (!spot) {
      report.overflowCount = (report.overflowCount ?? 0) + 1;
      report.warnings.push(`Could not place "${el.label}" — no free space on stage.`);
      return false;
    }
    const finalEl = { ...el, x: spot.x, y: spot.y };
    elements.push(finalEl);
    placed.push({ el: finalEl, left: spot.x, top: spot.y, right: spot.x + bw, bottom: spot.y + bh });
    report.placedCount += 1;
    return true;
  };

  // Stage too small guard: need at least ~3×3 units to place anything.
  if (stageW < 3 || stageH < 3) {
    report.stageTooSmall = true;
    report.warnings.push('Stage is too small to place any elements.');
    report.neededArea = '3 × 3';
    return { plot: buildPlot(input, elements), report };
  }

  // ── Back row: risers ──
  const risers = input.risers ?? [];
  for (const r of risers) {
    const rw = Math.max(0.5, parseRiserDimToMeters(r.width, 2));
    const rh = Math.max(0.5, parseRiserDimToMeters(r.depth, 1.5));
    const el = makeElement('risers', r.name || 'Riser', 0, 0);
    el.width = rw;
    el.height = rh;
    // A generated composite ALWAYS carries a valid config (canonical from real
    // data, falls back to defaultRisers on unparseable dims). The renderer
    // never receives a composite without one.
    el.config = riserConfigFromDraft(r) as unknown as Record<string, unknown>;
    add(el, (stageW - rw) / 2, 0.2);
  }

  // ── Back row: drum kit (centre) ──
  const drummers = input.musicians.filter((m) =>
    m.instruments.some((i) => /drum|batterie|kit/i.test(i.name)),
  );
  for (const d of drummers) {
    const el = makeElement('kick', d.name || 'Drums', 0, 0);
    add(el, (stageW - el.width) / 2, 0.4);
  }

  // ── Back row: distro / rack (garden side = right) ──
  const distro = makeElement('distro', 'Power Distro', 0, 0);
  add(distro, stageW - distro.width - 0.3, 0.3);

  // ── Musicians (non-drummer) ──
  const nonDrummers = input.musicians.filter(
    (m) => !m.instruments.some((i) => /drum|batterie|kit/i.test(i.name)),
  );
  const midY = stageH * 0.45;
  const frontY = stageH * 0.7;
  let midIndex = 0;
  let frontIndex = 0;
  const midCount = nonDrummers.length;

  for (const m of nonDrummers) {
    const primary = m.instruments[0];
    const match = primary ? iconForInstrumentName(primary.name) : { iconId: null };
    if (!match.iconId) {
      report.unmapped.push({ name: m.name, reason: `No icon for instrument "${primary?.name ?? 'none'}"` });
      continue;
    }
    const el = makeElement(match.iconId, m.name || primary.name);
    const amp = primary ? ampIconForInstrumentName(primary.name) : null;

    // Keyboard / synth players sit mid-stage; everyone else on the front line.
    const isKeys = /keys|keyboard|synth|mpc|sampler|modular|piano|organ/i.test(primary?.name ?? '');
    if (isKeys) {
      const x = stageW * (0.25 + 0.5 * (midIndex / Math.max(1, midCount)));
      add(el, x, midY);
      midIndex += 1;
    } else {
      const x = stageW * (0.2 + 0.6 * (frontIndex / Math.max(1, midCount)));
      add(el, x, frontY);
      frontIndex += 1;
    }

    // Amp behind the player (guitar/bass).
    if (amp) {
      const ampEl = makeElement(amp, `${m.name} amp`, el.x, Math.max(0, el.y - el.height - 0.3));
      add(ampEl, ampEl.x, ampEl.y);
    }
  }

  // ── Crew on stage ──
  const includeCrew = input.crew ?? [];
  for (const c of includeCrew) {
    const role = iconForRole(c.role);
    if (!role.onStage) continue;
    if (role.iconId) {
      // Console on the garden side (right), mid-stage.
      const el = makeElement(role.iconId, c.name || c.role, 0, 0);
      add(el, stageW - el.width - 0.3, stageH * 0.4);
    } else {
      // stagehand / tech: no element, but mention in the report.
      report.warnings.push(`${c.name || c.role} (${c.role}) works on stage — no element placed.`);
    }
  }

  // ── Backline items ──
  for (const b of input.backlineItems ?? []) {
    const qty = Math.max(1, b.quantity || 1);
    const match = iconForInstrumentName(b.label || b.type);
    if (!match.iconId) {
      report.unmapped.push({ name: b.label || b.type, reason: 'No icon for this backline item' });
      continue;
    }
    for (let i = 0; i < qty; i++) {
      const el = makeElement(match.iconId, b.label || b.type, 0, 0);
      // Wedges go along the front edge; everything else fills remaining space.
      if (/wedge|monitor/i.test(b.label || b.type)) {
        add(el, 0.3 + i * (el.width + 0.3), stageH - el.height - 0.2);
      } else {
        add(el, 0.3 + (i % 3) * (el.width + 0.3), stageH * 0.85);
      }
    }
  }

  // ── Sidefills (lateral) ──
  const sidefillL = makeElement('sidefill', 'Sidefill', 0, 0);
  add(sidefillL, 0.2, stageH * 0.5);
  const sidefillR = makeElement('sidefill', 'Sidefill', 0, 0);
  add(sidefillR, stageW - sidefillR.width - 0.2, stageH * 0.5);

  // ── Wedges along the front edge (1 per amplified musician, max 6) ──
  const amplified = input.musicians.filter((m) =>
    m.instruments.some((i) => /guitar|bass|keys|keyboard|synth|vocal|mpc|sampler/i.test(i.name)),
  );
  const wedgeCount = Math.min(6, amplified.length);
  for (let i = 0; i < wedgeCount; i++) {
    const el = makeElement('wedge', 'Wedge', 0, 0);
    add(el, 0.3 + i * (el.width + 0.3), stageH - el.height - 0.2);
  }

  return { plot: buildPlot(input, elements), report };
}

function buildPlot(input: StagePlotDraftInput, elements: StageElement[]): StagePlot {
  const now = Date.now();
  const name = input.plotName || `${input.bandName || 'Band'} Stage Plot (draft)`;
  const plot = createEmptyStagePlot(name);
  return {
    ...plot,
    name,
    stageDimensions: { ...input.stageDimensions },
    // Hard guarantee: no composite (risers/spd/drumkit) leaves the generator
    // without a valid config. Already-correct riser configs are preserved.
    elements: elements.map(normalizeElementConfig),
    metadata: {
      ...plot.metadata,
      bandName: input.bandName || '',
      created: now,
      lastModified: now,
    },
  };
}
